import { Link, Outlet, createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { LogOutIcon } from 'lucide-react'
import { StopImpersonating } from '@/components/auth/admin/stop-impersonating'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { authClient } from '@/lib/auth-client'
import { roles } from '@/lib/roles'
import logo from '@/assets/edclub-logo.png'

// Pathless layout: every route under routes/_authed/ requires a session.
// Only /login lives outside it, so without a session the app shows nothing else.
export const Route = createFileRoute('/_authed')({
	beforeLoad: async ({ location }) => {
		const { data } = await authClient.getSession()
		if (!data)
			throw redirect({
				to: '/login',
				search: location.href === '/' ? {} : { redirectTo: location.href },
				replace: true
			})
		return { user: data.user }
	},
	component: AdminShell
})

function AdminShell() {
	const { user } = Route.useRouteContext()
	const router = useRouter()

	const signOut = () => router.navigate({ to: '/logout', replace: true })

	return (
		<div className="flex min-h-svh flex-col bg-white">
			{/* A shadow hairline, not a border: the header stays exactly h-16, which the home page sizes itself against. */}
			<header className="sticky top-0 z-40 bg-white shadow-[0_1px_0_var(--color-border)]">
				<div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-8 px-8">
					{/* No nav links for now: the logo goes home, and the home search reaches every page. */}
					<Link to="/" className="flex shrink-0 items-center gap-3">
						<img src={logo} alt="edclub" className="h-7 w-auto" />
						<Badge variant="outline" className="gap-1.5 text-[11px] font-bold tracking-wider text-[#122947] uppercase">
							<span className="size-1.5 rounded-full bg-primary" />
							Admin
						</Badge>
					</Link>
					<DropdownMenu>
						<DropdownMenuTrigger className="ml-auto flex items-center gap-2.5 rounded-md px-2 py-1 text-left outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-[#122947]">
							<span className="hidden text-right leading-tight sm:grid">
								<span className="text-sm font-semibold text-[#122947]">{user.name}</span>
								<span className="text-xs text-muted-foreground">{user.email}</span>
							</span>
							<Avatar className="size-8">
								<AvatarFallback className="bg-[#122947] text-xs font-bold text-white">
									{initials(user.name || user.email)}
								</AvatarFallback>
							</Avatar>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							<DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
								Roles: {roles(user).join(', ') || 'user'}
							</DropdownMenuLabel>
							<StopImpersonating />
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={signOut}>
								<LogOutIcon className="text-muted-foreground" />
								Sign out
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</header>
			<main className="mx-auto w-full max-w-6xl p-8">
				<Outlet />
			</main>
		</div>
	)
}

const initials = (name: string) =>
	name
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.join('')
